import { Cell } from "../shared/interfaces";
import { ReferenceProcessor } from "./references";

/**
 * Downloads the specified table as a CSV.
 * @param dataTableId Table to download, specified by the data-table-id attribute
 * @param fileName Name of the downloaded file
 */
export function downloadTableAsCsv(dataTableId: string, fileName: string = "Marvin-table.csv") {
    const csv = getTableContents(dataTableId, ",");
    if (!csv) {
        return;
    }

    initiateCsvDownload(csv, fileName);
}

/**
 * Get the contents of a table
 * @param dataTableId Table to download, specified by the data-table-id attribute
 */
export function getTableContentsAsTabularText(dataTableId: string): string | null {
    return getTableContents(dataTableId, "\t");
}

export function getTableCellsAsTabularText(
    referenceProcessor: ReferenceProcessor,
    cells: Cell[][],
): string {
    return getTableContentsFromCells(referenceProcessor, cells, "\t");
}

export function downloadTableCellsAsCsv(
    referenceProcessor: ReferenceProcessor,
    cells: Cell[][],
    fileName: string = "Marvin-table.csv",
) {
    const csv = getTableContentsFromCells(referenceProcessor, cells, ",");
    initiateCsvDownload(csv, fileName);
}

/**
 * Initiates download of the provides CSV.
 */
function initiateCsvDownload(csv: string, fileName: string) {
    const BOM = "\uFEFF"; // NB: make sure to prepend the UTF-8 Byte Order Mark to ensure proper encoding in Excel
    const csvContent = BOM + csv;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute("download", fileName);
    a.click();
}

export function getTableContentsFromCells(
    referenceProcessor: ReferenceProcessor,
    cells: Cell[][],
    delimiter: string,
): string {
    return cells
        .map((row) =>
            row
                .map((cell) =>
                    // Combine content of each part in the cell, separating references and content parts by spaces
                    cell
                        .map((part) => {
                            const processedContent = part.isReference
                                ? referenceProcessor.getText(part.rawContent)
                                : part.rawContent;
                            return escapeCsvChars(processedContent, delimiter);
                        })
                        .join(" "),
                )
                .join(delimiter),
        )
        .join("\n");
}

/**
 * Get the contents of a table with a given delimiter.
 * @param dataTableId Table to download, specified by the data-table-id attribute
 * @param delimiter Delimiter to use. ',' for CSV, and '\t' for tabular.
 */
function getTableContents(dataTableId: string, delimiter: string): string | null {
    const tableElement = document.querySelector(
        `table[data-table-id="${dataTableId}"]`,
    ) as HTMLTableElement;
    if (!tableElement) {
        return null;
    }

    return Array.from(tableElement.rows)
        .map((row) => {
            const cells = Array.from(row.cells);
            return cells
                .map((cell) => escapeCsvChars((cell.textContent || "").trim(), delimiter))
                .join(delimiter);
        })
        .join("\n");
}

/**
 * Safe way to parse JSON.
 * @returns Parsed JSON object if able to parse successfully, null otherwise.
 */
export function tryParseJson(input: string): any {
    try {
        return JSON.parse(input);
    } catch (e) {
        return null;
    }
}

/**
 * Export constructability issues as CSV
 * @param issues Array of constructability issues to export
 * @param fileName What to name the file to be downloaded
 */
export function downloadProblemsAsCsv(
    issues: Array<any>,
    fileName: string = "constructability-review.csv",
) {
    const headers = ["Issue #", "Title", "Category", "Conflict likelihood", "Description"];
    const rows = issues.map((issue, index) => [
        `${index + 1}`,
        issue.title,
        issue.category,
        issue.severity,
        issue.description,
    ]);

    const csv = [
        headers.join(","),
        ...rows.map((row) => row.map((cell) => escapeCsvChars(cell, ",")).join(",")),
    ].join("\n");

    initiateCsvDownload(csv, fileName);
}

/**
 * CSV escaper.
 *
 * Modified from From https://blog.en.sheepcs.de/parsing-csv-data-in-javascript-typescript/
 */
function escapeCsvChars(inputText: string, delimiter: string = ","): string {
    const escapeCharacter = '"';
    let outputText = inputText;

    // If escape character is included it must be escaped by doubling it
    if (inputText.includes(escapeCharacter)) {
        outputText = outputText.replaceAll(escapeCharacter, escapeCharacter + escapeCharacter);
    }

    /*
     * Complete line must be double quoted if it includes an Escape character or a delimiter character
     */
    if (inputText.includes(delimiter) || inputText.includes(escapeCharacter)) {
        outputText = `"${outputText}"`;
    }

    return outputText;
}

/**
 * Get a human-readable time difference between two dates.
 */
export function timeDifference(start: Date, end: Date) {
    const totalSeconds = Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const minuteString = minutes > 0 ? `${minutes}m ` : "";
    return `${minuteString}${seconds}s`;
}
